import { AfterViewInit, Component, inject, OnInit } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Browser } from '@capacitor/browser';
import { ViewWillEnter } from '@ionic/angular';
import { RoutesConfigService } from '../../config/routes-config.service';
import { DataService } from '../../services/data.service';
import { UserService } from '../../services/user.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'app-desktopnav',
    templateUrl: './desktopnav.component.html',
    styleUrls: ['./desktopnav.component.scss'],
    standalone: false
})
export class DesktopnavComponent implements OnInit {
  private routesConfig = inject(RoutesConfigService);
  public _show:boolean = false;
  public unreadMessagesCount:number = 0;

  get show(){
    // console.log('DesktopnavComponent show '+this.router.url);
    if(
      this.router.url == '/welcomequestionnaire' 
      ||
      this.router.url == '/welcomequestions' 
      || 
      this.router.url == '/fakesplash' 
      || 
      this.router.url == '/login' 
      || 
      this.router.url == '/agreement/age-agreement' 
      || 
      this.router.url == '/register' 
      || 
      this.router.url == '/splash' 
      || 
      this.router.url == '/accesscodes'  
      || 
      this.router.url == '/recoverytimeline'    
      || 
      this.router.url == '/introslider'  
      || 
      this.router.url == '/ppolicy'  
      || 
      this.router.url == '/termsandconditions'  
      || 
      this.router.url == '/guest'  
      || 
      this.router.url.startsWith('/guest?')
      || 
      this.router.url == '/postcode-entry'
      || 
      this.router.url.indexOf('/select-practice') != -1
      || 
      this.router.url == '/select-area'
      || 
      this.router.url == '/create-account'
      || 
      this.router.url.match(/\/gateway\/\d+/) != null
      || 
      this.router.url == '/need-help'
      || 
      this.router.url == '/need-help-thankyou'
      || 
      this.router.url == '/feedbackinput'
      ||
      this.router.url == '/local-service-options'
      ||
      this.router.url == '/select-super-organisation'
      ||
      this.router.url == '/welcome'
      ||
      this.router.url == '/nhs-callback'
      ||
      this.router.url.indexOf('/enter-code/') != -1
      ||
      this.router.url.indexOf('/verify/') != -1
      ||
      this.router.url.match(/\/guest(\?.*)?/)
      ||
      this.routesConfig.underGuestRoute(this.router.url)
      )
      this._show = false
    else
      this._show = true

    return this._show;
  }

  get hasUnreadMessages(): boolean {
    return this.unreadMessagesCount > 0;
  }

  /**
   * Logout only when the desktop nav is visible and the user has an authenticated session.
   * Public routes can still show the nav chrome without a JWT (`token` / `isUserLogged`).
   */
  get showLogout(): boolean {
    return this.show && !!this.data.token && this.data.isUserLogged;
  }

  constructor(
    public router: Router,
    public data: DataService,
    public userService: UserService,
    private translate: TranslateService
    ) { 

      this.router.events.subscribe((event) => {
        if(event instanceof NavigationEnd){
          if(
            this.router.url == '/welcomequestionnaire' 
            ||
            this.router.url == '/welcomequestions' 
            || 
            this.router.url == '/fakesplash' 
            || 
            this.router.url == '/login' 
            || 
            this.router.url == '/register' 
            || 
            this.router.url == '/splash' 
            || 
            this.router.url == '/accesscodes'  
            || 
            this.router.url == '/recoverytimeline'    
            || 
            this.router.url == '/introslider'  
            || 
            this.router.url == '/ppolicy'  
            || 
            this.router.url == '/termsandconditions'  
            || 
            this.router.url == '/guest'  
            || 
            this.router.url == '/postcode-entry'
            || 
            this.router.url.indexOf('/select-practice') != -1
            || 
            this.router.url == '/select-area'
            || 
            this.router.url == '/create-account'
            || 
            this.router.url == '/need-help'
            || 
            this.router.url == '/need-help-thankyou'
            || 
            this.router.url == '/feedbackinput'
            ||
            this.router.url == '/local-service-options'
            ||
            this.router.url == '/select-super-organisation'
            ||
            this.router.url == '/welcome'
            ||
            this.router.url == '/nhs-callback'
            ||
            this.router.url.indexOf('/enter-code/') != -1
            ||
            this.router.url.indexOf('/verify/') != -1
            ||
            this.routesConfig.underGuestRoute(this.router.url)
            )
            this._show = false
          else
            this._show = true
        }
    });

  }

  private normalizeMenuLabel(value: string): string {
    if (!value) {
      return '';
    }

    const container = document.createElement('div');
    container.innerHTML = value;

    return container.textContent?.trim() || value;
  }

  label(name):string{
    const key = this.resolveMenuTranslationKey(name);
    if (key) {
      return this.translate.instant(key);
    }

    if(this.data.menu){
      let found = this.data.menu.find((e) => e.name == name)
      if(found && found.custom)
        return this.normalizeMenuLabel(found.custom)
    }

    return name
  }

  private resolveMenuTranslationKey(input: string): string | null {
    const normalized = (input || '').trim().toLowerCase();
    const map: Record<string, string> = {
      'home': 'nav.dashboard',
      'dashboard': 'nav.dashboard',
      'inbox': 'nav.inbox',
      'my recovery': 'nav.my_treatment',
      'my treatment': 'nav.my_treatment',
      'infozone': 'nav.information',
      'information': 'nav.information',
      'treatmentzone': 'nav.local_treatment',
      'local treatment': 'nav.local_treatment',
      'feedbackzone': 'nav.feedback',
      'feedback': 'nav.feedback',
      'my gp practice': 'nav.my_gp',
      'settings': 'nav.settings',
      'nhs login settings': 'nav.nhs_settings',
      'help section': 'nav.help',
      'my referrals': 'nav.appointments',
      'my appointments': 'nav.appointments',
      'change condition': 'nav.change_condition',
      'logout': 'common.logout',
    };

    return map[normalized] || null;
  }
  
  async logout(){
    await this.data.logout(true, true, '', true);
  }

  openUrl(url: string) {
    Browser.open({ 
      url: url, 
      // target="_blank" rel="noopener noreferrer" 
      windowName: '_blank',
     });
  }

  ngOnInit() {
    console.log('DesktopnavComponent ionViewWillEnter');
    this.unreadMessagesCount = this.data.letters.filter((e) => !e.read)?.length || 0;

    // subscribe to view updates
    this.userService.letters$.subscribe((letters) => {
      this.unreadMessagesCount = letters.filter((e) => !e.read)?.length || 0;
    });
  
  }

}
